import React, { useState, useCallback, useEffect, HTMLAttributes } from 'react';
import { AxiosResponse } from 'axios';
import useApiFetch from '@/hooks/useApiFetch';
import candidatesServices, { Candidate } from '@/services/candidates';
import { FetchApiIds } from '@/types';
import PersonSearchComponent, { Person } from './PersonSearch.component';
import { shouldSearchPersonResults } from './PersonSearch.utils';

export interface PersonSearchContainerProps extends HTMLAttributes<HTMLInputElement> {
  selectedPerson?: Person | null;
  placeholder?: string;
  onPersonSelect: (person: Person) => void;
  errorMessage?: string;
  name?: string;
  label?: string;
}

const PersonSearchContainer = ({
  selectedPerson,
  name,
  label,
  placeholder,
  onPersonSelect,
  errorMessage,
  ...props
}: PersonSearchContainerProps): JSX.Element => {
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [showResults, setShowResults] = useState(false);

  useEffect(() => {
    if (!selectedPerson) {
      setSearchTerm('');
      return;
    }

    setSearchTerm(`${selectedPerson.firstName} ${selectedPerson.lastName}`);
  }, [selectedPerson]);

  const { data: personSearchResponse, isLoading: personSearchLoading } = useApiFetch(
    [FetchApiIds.CANDIDATE_SEARCH, searchTerm],
    async () => {
      const [nameResponse, skillResponse] = await Promise.all([
        candidatesServices.searchCandidate(searchTerm),
        candidatesServices.searchCandidatesBySkill(searchTerm),
      ]);

      const nameCandidates = nameResponse.data ?? [];
      const skillCandidates = skillResponse.data ?? [];

      const candidateMap = new Map();
      [...nameCandidates, ...skillCandidates].forEach((candidate) => {
        if (!candidateMap.has(candidate.id)) {
          candidateMap.set(candidate.id, candidate);
        }
      });

      return { data: Array.from(candidateMap.values()) } as AxiosResponse<Candidate[]>;
    },
    { enabled: shouldSearchPersonResults(searchTerm) }
  );

  const persons = (personSearchResponse || []).map((person) => ({
    id: person.id,
    firstName: person.firstName,
    lastName: person.lastName,
    developerType: person.developerType || undefined,
    pictureUrl: person.pictureUrl ?? '',
  }));

  const handleSearchChange = useCallback((event: React.ChangeEvent<HTMLInputElement>): void => {
    const { value } = event.target;
    setSearchTerm(value);
    setShowResults(value.length >= 3);
    if (value.length < 3) {
      setShowResults(false);
    }
  }, []);

  const handlePersonSelect = useCallback(
    (person: Person): void => {
      setSearchTerm(`${person.firstName} ${person.lastName}`);
      setShowResults(false);
      onPersonSelect(person);
    },
    [onPersonSelect]
  );

  return (
    <PersonSearchComponent
      name={name}
      label={label}
      searchTerm={searchTerm}
      showResults={showResults}
      persons={persons}
      isLoading={personSearchLoading}
      placeholder={placeholder}
      onSearchChange={handleSearchChange}
      onPersonSelect={handlePersonSelect}
      errorMessage={errorMessage}
      {...props}
    />
  );
};

export default PersonSearchContainer;
